/**
 * AdminGpEarnings — Iter 81.
 *
 * Clinic-wide dashboard surfacing the value delivered by each referring GP:
 *   • Referrals submitted via the public /refer/{code} flow
 *   • Patients under care + sessions completed + clinic revenue
 *   • Recovery-rate proxy (≥ 2-point NPRS drop across recorded outcomes)
 *   • 30-day outcome reports already e-mailed
 *
 * Read-only for now — payouts hang off this same data model when the
 * eventual referral-fee schedule lands.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Users, BadgeDollarSign, Activity, Mail, Loader2, ExternalLink, Calendar, Download, Settings2, Save, Trash2, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { api, BACKEND_URL } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useAuth } from '../../lib/auth';
import { safeConfirm } from '../../lib/confirm';

const fmtCurrency = (n) => new Intl.NumberFormat(undefined, {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
}).format(Number(n) || 0);

export default function AdminGpEarnings() {
  const { locale } = useAuth();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFeeEditor, setShowFeeEditor] = useState(false);
  const [feeRule, setFeeRule] = useState(null);
  const [thankYouRunning, setThankYouRunning] = useState(false);
  const sendThankYouNow = async () => {
    if (!await safeConfirm("Send the quarterly thank-you nudge to every GP in your clinic who has at least one recovered patient in the last 90 days? Each GP is only emailed once per quarter — repeats this quarter will be skipped.")) return;
    setThankYouRunning(true);
    try {
      const { data } = await api.post('/gp/thank-you/run-now', {});
      const n = data.sent_count || 0;
      toast.success(n ? `Sent ${n} thank-you email${n === 1 ? '' : 's'}.` : 'No GPs eligible — everyone already nudged this quarter, or no patients recovered yet.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setThankYouRunning(false); }
  };

  useEffect(() => {
    let stop = false;
    Promise.all([
      api.get('/gp/earnings/summary'),
      api.get('/gp/earnings/per-gp'),
      api.get('/referral-fees/schedule').catch(() => ({ data: null })),
    ]).then(([s, r, f]) => {
      if (stop) return;
      setSummary(s.data); setRows(r.data || []); setFeeRule(f.data || null);
    }).finally(() => { if (!stop) setLoading(false); });
    return () => { stop = true; };
  }, []);

  const exportCsv = () => {
    // Re-use the user's session by hitting the API with credentials and turning
    // the response into a blob the browser saves. Trying to <a download> the
    // URL directly would lose the Authorization header.
    api.get('/gp/earnings/export.csv', { responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gp-earnings-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        toast.success('Export downloaded.');
      })
      .catch(() => toast.error('Export failed.'));
  };

  const saveFeeRule = async (rule) => {
    try {
      const { data } = await api.put('/referral-fees/schedule', rule);
      setFeeRule(data);
      toast.success('Fee schedule saved — earnings refreshing…');
      // Re-fetch earnings so the fee column updates.
      const r = await api.get('/gp/earnings/per-gp');
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || 'Save failed');
    }
  };

  const disableFeeRule = async () => {
    if (!await safeConfirm('Disable the current fee schedule? GPs will no longer accrue fees.')) return;
    try {
      await api.delete('/referral-fees/schedule');
      setFeeRule(null);
      toast.success('Fee schedule disabled.');
    } catch (e) {
      toast.error('Disable failed.');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-fg-muted text-sm" data-testid="gp-earnings-loading">
        <Loader2 className="w-4 h-4 animate-spin"/> Loading GP earnings…
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl" data-testid="gp-earnings">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">{t(locale, 'gp_referral_flywheel')}</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">
            {t(locale, 'gp_earnings_title')}
          </h1>
          <p className="text-fg-muted text-sm mt-1 max-w-2xl">
            {t(locale, 'gp_earnings_lede')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv}
                  className="btn-secondary inline-flex items-center gap-2 text-xs"
                  data-testid="gp-earnings-export-csv">
            <Download className="w-4 h-4"/> {t(locale, 'export_csv')}
          </button>
          <button onClick={sendThankYouNow} disabled={thankYouRunning}
                  className="btn-secondary inline-flex items-center gap-2 text-xs disabled:opacity-40"
                  title="Send the quarterly thank-you nudge to every eligible GP"
                  data-testid="gp-earnings-send-thank-you">
            {thankYouRunning
              ? <Loader2 className="w-4 h-4 animate-spin"/>
              : <Heart className="w-4 h-4"/>}
            Send thank-you now
          </button>
          <button onClick={() => setShowFeeEditor(true)}
                  className="btn-secondary inline-flex items-center gap-2 text-xs"
                  data-testid="gp-earnings-open-fee-editor">
            <Settings2 className="w-4 h-4"/> {t(locale, 'fee_schedule')}
            {feeRule?.active && (
              <span className="ml-1 inline-block w-1.5 h-1.5 bg-[#1B5E20] rounded-full" aria-label="active"/>
            )}
          </button>
        </div>
      </div>

      {/* Headline tiles */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="gp-earnings-tiles">
        <Tile icon={Users} label={t(locale, 'active_gps')}
              value={summary?.total_gps_active || 0}
              hint="GPs who've sent ≥ 1 inbound referral" testidSlug="active-gps"/>
        <Tile icon={TrendingUp} label={t(locale, 'referrals_30d')}
              value={summary?.total_referrals_last_30d || 0}
              hint={`${summary?.total_referrals_all_time || 0} all-time`} testidSlug="referrals-30-d"/>
        <Tile icon={Activity} label={t(locale, 'sessions_delivered')}
              value={summary?.total_sessions_completed || 0}
              hint="Completed appointments" testidSlug="sessions-delivered"/>
        <Tile icon={BadgeDollarSign} label={t(locale, 'clinic_revenue')}
              value={fmtCurrency(summary?.total_revenue || 0)}
              hint={summary?.avg_recovery_rate_pct != null
                ? `Avg recovery ${summary.avg_recovery_rate_pct}%`
                : 'Recovery insufficient data'} testidSlug="clinic-revenue"/>
      </div>

      {/* Top GP highlight */}
      {summary?.top_gp && summary.top_gp.sessions_completed > 0 && (
        <div className="bento-card mb-6 border-l-4 border-accent" data-testid="gp-earnings-top">
          <div className="text-xs uppercase tracking-wider text-accent">{t(locale, 'top_referrer')}</div>
          <div className="font-heading font-bold text-lg mt-1">
            {summary.top_gp.gp_name || summary.top_gp.gp_email}
          </div>
          <div className="text-sm text-fg-muted">{summary.top_gp.gp_practice || '—'}</div>
          <div className="grid sm:grid-cols-4 gap-3 mt-3 text-sm">
            <Mini label={t(locale, 'referrals')} value={summary.top_gp.total_referrals_submitted}/>
            <Mini label={t(locale, 'sessions')} value={summary.top_gp.sessions_completed}/>
            <Mini label={t(locale, 'recovery_pct')} value={summary.top_gp.recovery_rate_pct != null ? `${summary.top_gp.recovery_rate_pct}%` : '—'}/>
            <Mini label={t(locale, 'revenue')} value={fmtCurrency(summary.top_gp.clinic_revenue_total)}/>
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <div className="bento-card" data-testid="gp-earnings-table">
        <h3 className="font-heading font-semibold mb-3">{t(locale, 'gp_leaderboard')}</h3>
        {rows.length === 0 ? (
          <p className="text-sm text-fg-muted">{t(locale, 'no_gp_yet')}</p>
        ) : (
          <div className="overflow-x-auto -mx-4">
            <table className="w-full text-sm">
              <thead className="text-xs text-fg-muted uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 text-left">GP</th>
                  <th className="px-4 py-2 text-right">{t(locale, 'referrals')}</th>
                  <th className="px-4 py-2 text-right">Patients</th>
                  <th className="px-4 py-2 text-right">{t(locale, 'sessions')}</th>
                  <th className="px-4 py-2 text-right">{t(locale, 'recovery_pct')}</th>
                  <th className="px-4 py-2 text-right">{t(locale, 'reports_sent')}</th>
                  <th className="px-4 py-2 text-right">{t(locale, 'revenue')}</th>
                  {feeRule?.active && (
                    <th className="px-4 py-2 text-right" data-testid="gp-col-fee">{t(locale, 'fee_owed')}</th>
                  )}
                  <th className="px-4 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.gp_email} className="border-t border-soft hover:bg-bg-secondary/30"
                      data-testid={`gp-row-${r.code}`}>
                    <td className="px-4 py-2">
                      <div className="font-semibold">{r.gp_name || r.gp_email}</div>
                      <div className="text-[11px] text-fg-muted">{r.gp_practice || r.gp_email}</div>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.total_referrals_submitted}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.patients_under_care}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.sessions_completed}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {r.recovery_rate_pct != null ? `${r.recovery_rate_pct}%` : <span className="text-fg-muted">—</span>}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.outcomes_reports_sent}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold">{fmtCurrency(r.clinic_revenue_total)}</td>
                    {feeRule?.active && (
                      <td className="px-4 py-2 text-right tabular-nums text-[#1B5E20] font-semibold"
                          data-testid={`gp-fee-${r.code}`}>
                        {fmtCurrency(r.referral_fee_owed || 0)}
                      </td>
                    )}
                    <td className="px-4 py-2 text-right">
                      <Link to={`/admin/gp-inbound?gp=${encodeURIComponent(r.gp_email)}`}
                            className="text-brand inline-flex items-center gap-1 text-xs"
                            data-testid={`gp-row-open-${r.code}`}>
                        Inbox <ExternalLink className="w-3 h-3"/>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 text-[11px] text-fg-muted">
        <Calendar className="inline-block w-3 h-3 -mt-0.5 mr-1"/>
        Recovery % is a proxy: % of this GP's patients whose NPRS pain score dropped
        by ≥ 2 points across recorded outcomes. Switch to a custom outcome measure in
        clinic settings.
      </div>

      {showFeeEditor && (
        <FeeScheduleEditor
          rule={feeRule}
          onClose={() => setShowFeeEditor(false)}
          onSave={async (r) => { await saveFeeRule(r); setShowFeeEditor(false); }}
          onDisable={async () => { await disableFeeRule(); setShowFeeEditor(false); }}
        />
      )}
    </div>
  );
}

function FeeScheduleEditor({ rule, onClose, onSave, onDisable }) {
  const [mode, setMode] = useState(rule?.mode || 'per_patient');
  const [unit, setUnit] = useState(rule?.fee_per_unit ?? 25);
  const [cap, setCap] = useState(rule?.cap_total ?? '');
  const [note, setNote] = useState(rule?.note || '');
  const [active, setActive] = useState(rule?.active ?? true);
  const unitLabel = mode === 'revenue_share' ? '% of clinic revenue' : '€ per ' + mode.replace('per_', '');
  const help = {
    per_patient:   'Pay the GP a fixed fee for every patient they refer who attends ≥ 1 session.',
    per_session:   'Pay the GP a fee for every session their referred patients complete.',
    revenue_share: 'Pay the GP a percentage of the revenue you book from their patients.',
  }[mode];

  const submit = (e) => {
    e.preventDefault();
    onSave({
      mode, fee_per_unit: Number(unit),
      cap_total: cap === '' ? null : Number(cap),
      currency: 'EUR', note, active,
    });
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="fee-editor">
      <div className="bg-bg-primary border border-soft rounded-lg shadow-xl max-w-md w-full"
           onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-lg">Referral-fee schedule</h3>
            <button type="button" onClick={onClose} className="text-fg-muted hover:text-fg" data-testid="fee-editor-close">✕</button>
          </div>
          <p className="text-xs text-fg-muted">
            Configure how each referring GP gets paid. Earnings appear in the
            <strong> Fee owed</strong> column and the CSV export. Disable the rule
            anytime — historical referrals are not modified.
          </p>

          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Mode</div>
            <select className="input" value={mode}
                    onChange={(e) => setMode(e.target.value)}
                    data-testid="fee-editor-mode">
              <option value="per_patient">Per converted patient</option>
              <option value="per_session">Per completed session</option>
              <option value="revenue_share">Revenue share (%)</option>
            </select>
            <div className="text-[11px] text-fg-muted mt-1">{help}</div>
          </label>

          <label className="block">
            <div className="text-xs text-fg-muted mb-1">{unitLabel}</div>
            <input className="input" type="number" min="0"
                   step={mode === 'revenue_share' ? '0.1' : '1'}
                   max={mode === 'revenue_share' ? 100 : undefined}
                   value={unit}
                   onChange={(e) => setUnit(e.target.value)}
                   data-testid="fee-editor-unit"
                   required/>
          </label>

          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Cap (€, optional)</div>
            <input className="input" type="number" min="0"
                   value={cap}
                   onChange={(e) => setCap(e.target.value)}
                   placeholder="Leave blank for no cap"
                   data-testid="fee-editor-cap"/>
          </label>

          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Note (internal)</div>
            <input className="input" value={note}
                   onChange={(e) => setNote(e.target.value)}
                   placeholder="e.g. €25 per converted patient — Q1 trial"
                   data-testid="fee-editor-note"/>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={active}
                   onChange={(e) => setActive(e.target.checked)}
                   data-testid="fee-editor-active"/>
            Active (uncheck to pause without losing the rule)
          </label>

          <div className="flex items-center justify-between gap-2 pt-2 border-t border-soft">
            {rule?.active && (
              <button type="button" onClick={onDisable}
                      className="text-xs text-[#B71C1C] inline-flex items-center gap-1"
                      data-testid="fee-editor-disable">
                <Trash2 className="w-3.5 h-3.5"/> Disable
              </button>
            )}
            <div className="ml-auto flex gap-2">
              <button type="button" onClick={onClose} className="btn-secondary text-xs">Cancel</button>
              <button type="submit" className="btn-primary inline-flex items-center gap-1 text-xs"
                      data-testid="fee-editor-save">
                <Save className="w-4 h-4"/> Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, hint, testidSlug }) {
  // Iter 81/83 — testid uses an explicit slug prop so it stays stable across
  // locales (PL/ES labels can't be auto-slugged to the same English keys).
  const slug = testidSlug || label.toLowerCase()
    .replace(/[()]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return (
    <div className="bento-card flex flex-col gap-1" data-testid={`gp-tile-${slug}`}>
      <div className="text-xs text-fg-muted flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5"/> {label}
      </div>
      <div className="text-2xl font-bold font-mono text-brand">{value}</div>
      {hint && <div className="text-[11px] text-fg-muted">{hint}</div>}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-fg-muted">{label}</div>
      <div className="text-base font-semibold">{value}</div>
    </div>
  );
}
